import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AxiosResponse } from 'axios';
import TextValue from '../TextValue';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../../theme/ui/Atoms/Button';
import Input from '../../../../theme/ui/Atoms/Input';
import useBack from '../../../../shared/util/route-dom/hooks/useBack';
import BackButton from '../BackButton';
import ContainerBox from '../ContainerBox';
import { GroupAPIResponse } from '../../../../service/group/group.model';
import { ScanAirConnectIcon } from '../../../../assets';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import StoreStructureAPI from '../../../../redux/structure/store/StoreStructureAPI';
import useMyWorkspace from '../../../../redux/workspace/hooks/useMyWorkspace';
import AlertMessage from '../AlertMessage';
import GroupAPI from '../../../../service/group/GroupAPI';
import { fromBase64 } from '../../../../shared/util/base64';
import { CommanderAPI } from '../../../../service/device/CommanderAPI/CommanderAPI';
import FlexeserveDeviceAPI from '../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import {
  getExtendedSerialNumber,
  INSTALLATION_TYPE,
  isCreating,
  isUpdating,
} from '../utils';
import { ModalInfo } from '../../../../theme/ui/Atoms/Modal';

const validationSchema = yup.object({
  externalStoreId: yup
    .string()
    .required('initial_setup.search_store.store_id_required'),
});
function SearchStoreSetUp() {
  const { installationType = INSTALLATION_TYPE.store } = useParams<{
    installationType: INSTALLATION_TYPE;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showError = useErrorMessage();

  const [disabled, setDisabled] = React.useState<boolean>(false);
  const [showModal, setShowModal] = React.useState<boolean>(false);

  const {
    data: myWorkspace,
    isLoading: isLoadingMyWorkspace,
    error: errorMyWorkspace,
  } = useMyWorkspace(undefined, { refetchOnMountOrArgChange: true });

  const [triggerGetDevices, resultDevices] =
    FlexeserveDeviceAPI.useLazyGetStoreDevicesQuery();
  const [
    triggerGetDeviceInformation,
    { data: resultDeviceInformation, error: errorDeviceInformation },
  ] = CommanderAPI.useLazyGetDeviceByIdQuery();
  const [triggerGetStore, resultStore] =
    StoreStructureAPI.useLazyGetStoreMetadataByExternalIdQuery();

  const [createGroup, { error: errorCreatingGroup }] =
    GroupAPI.useCreateGroupMutation();
  const [linkStoreToGroup, { error: errorLinkingStoreToGroup }] =
    StoreStructureAPI.useLinkStoreMetadataToAGroupMutation();

  const {
    error: errorStore,
    data: storeMetadata,
    isFetching: isFetchingStore,
    isSuccess: isSuccessStore,
  } = resultStore;

  const [fetchStoreAudit] = StoreStructureAPI.useLazyGetStoreAuditQuery();

  const { error: errorStoreDevices } = resultDevices;
  let errorEmptyStoreDevices: Error | undefined;
  const address = [
    storeMetadata?.street,
    storeMetadata?.customData?.City,
    storeMetadata?.customData?.State,
    storeMetadata?.zipcode,
    storeMetadata?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const getStoreCommander = useCallback(
    (storeId: string) => {
      return triggerGetDevices(storeId ?? '')
        .unwrap()
        .then((devices) => {
          if (devices?.length) {
            // Get the commanderId from the first device, we assume that all stores have just one device
            const commanderId = devices[0].commanderId;
            return triggerGetDeviceInformation(commanderId).unwrap();
          } else {
            throw new Error('initial_setup.search_store.no_devices_found');
          }
        })
        .then(() => storeMetadata);
    },
    [triggerGetDevices, triggerGetDeviceInformation, storeMetadata]
  );

  const catchEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  const errorMitigated =
    // eslint-disable-next-line eqeqeq
    (errorStore as AxiosResponse)?.status == 404 ||
    (isSuccessStore && !storeMetadata)
      ? new Error('initial_setup.search_store.store_not_found')
      : errorStore;

  const error =
    errorMitigated ||
    errorMyWorkspace ||
    errorCreatingGroup ||
    errorLinkingStoreToGroup ||
    errorStoreDevices ||
    errorEmptyStoreDevices ||
    errorDeviceInformation;

  const formik = useFormik<{
    store: GroupAPIResponse | null;
    storeId: string;
    externalStoreId: string;
  }>({
    initialValues: {
      store: null,
      storeId: '',
      externalStoreId: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setDisabled(false);
      await triggerGetStore({
        externalId: values.externalStoreId.trim(),
        workspaceId: myWorkspace?.id ?? '',
      })
        .unwrap()

        .then((storeMetadata) => {
          // create group if not found
          const storeId = storeMetadata?.uuid;

          if (isUpdating(installationType)) {
            return getStoreCommander(storeId ?? '').catch(() => {
              setDisabled(true);
            });
          }
          if (isCreating(installationType) && storeId) {
            return fetchStoreAudit({
              workspaceId: myWorkspace?.id ?? '',
              groupId: storeMetadata?.uuid ?? '',
            })
              .unwrap()
              .then((storeAudit) => {
                if (storeAudit?.length > 0) {
                  setDisabled(true);
                  return getStoreCommander(storeMetadata?.uuid ?? '')
                    .then(() => {
                      setShowModal(true);
                    })
                    .catch((error) => {
                      setDisabled(false);
                      showError(error);
                    });
                }
              })
              .catch(() => {
                setDisabled(false);
              });
          }

          if (!storeId && isCreating(installationType)) {
            // If the store is not found, we create a group and link the store to it
            return createGroup({
              name: storeMetadata?.storeName ?? '',
              parentGroupID: storeMetadata?.regionId,
              attributes: {
                FS_Store_ID: values.externalStoreId,
              },
            })
              .unwrap()
              .then((group) =>
                linkStoreToGroup({
                  externalId: values.externalStoreId,
                  groupId: fromBase64(group.newID),
                  workspaceId: myWorkspace?.id ?? '',
                }).unwrap()
              )
              .then(() => {
                return triggerGetStore({
                  externalId: values.externalStoreId,
                  workspaceId: myWorkspace?.id ?? '',
                }).unwrap();
              })
              .then((storeMetadata) => {
                return storeMetadata;
              });
          }
          return storeMetadata;
        })

        .then((storeMetadata) => {
          formik.setFieldValue('storeId', storeMetadata?.uuid ?? '');
        })
        .catch((error) => showError(error));
    },
  });
  const onClickBack = useBack('/');

  const onClickNext = () => {
    if (isCreating(installationType)) {
      navigateToSearchCommander();
    } else {
      navigateToCheckList();
    }
  };

  const navigateToSearchCommander = () => {
    navigate(`../store/${storeMetadata?.uuid}/search-commander`);
  };

  const navigateToCheckList = (updateRoute = false) => {
    const extendedSerialNumber = getExtendedSerialNumber(
      resultDeviceInformation?.serialNumber ?? ''
    );
    const deviceId = fromBase64(resultDeviceInformation?.uuid ?? '');
    if (!deviceId.length) {
      navigateToSearchCommander();
      return;
    }
    const route = updateRoute ? '../../update/commander' : '../commander';

    navigate(
      `${route}/${deviceId}/serialNumber/${extendedSerialNumber}/store/${storeMetadata?.uuid}/check-list`
    );
  };

  const disabledError: boolean =
    !!(errorStore as AxiosResponse) ||
    !storeMetadata ||
    !!errorStoreDevices ||
    !!errorDeviceInformation;

  return (
    <>
      <ContainerBox maxWidth="sm">
        <Typography variant="h1">
          {isUpdating(installationType)
            ? t('initial_setup.connect_commander.update_title')
            : t('initial_setup.connect_commander.title')}
        </Typography>
        <Grid container spacing={2} my={4}>
          <Grid item xs>
            <Input
              label={t('initial_setup.search_store.store_id_label')}
              fullWidth
              required
              aria-required
              name="externalStoreId"
              value={formik.values.externalStoreId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onKeyDown={catchEnter}
              error={
                (formik.touched.storeId && Boolean(formik.errors.storeId)) ||
                Boolean(errorMitigated)
              }
              helperText={
                (formik.touched.storeId &&
                  formik.errors.storeId &&
                  t(formik.errors.storeId)) ||
                ''
              }
            />
          </Grid>

          <Grid item width={'150px'} mt={3}>
            <Button
              variant="contained"
              color="primary"
              rounded
              fullWidth
              onClick={() => formik.handleSubmit()}
              loading={isFetchingStore || formik.isSubmitting}
            >
              <ScanAirConnectIcon height={'2em'} />
            </Button>
          </Grid>
        </Grid>

        <Divider />
        <Box mt={2} minHeight={'200px'} my={4}>
          {!!error && <AlertMessage error={error} />}
          {!!storeMetadata && (
            <>
              <Grid container spacing={2} mt={1}>
                <TextValue
                  label={t('initial_setup.store.name') + ':'}
                  value={storeMetadata?.storeName}
                />

                <TextValue
                  label={t('initial_setup.store.bu') + ':'}
                  value={storeMetadata?.customData?.['Business Unit']}
                />
                <TextValue
                  label={t('initial_setup.store.address') + ':'}
                  value={address}
                />
                <TextValue
                  label={t('initial_setup.store.email') + ':'}
                  value={storeMetadata?.customData?.['Store e-mail']}
                />

                <TextValue
                  label={t('initial_setup.store.connect_email') + ':'}
                  value={storeMetadata?.customData?.['Operator log-in name']}
                />
              </Grid>
            </>
          )}
        </Box>

        <Grid container spacing={2} justifyContent={'space-between'}>
          <Grid item xs={12} sm={4}>
            <BackButton onClick={onClickBack} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              rounded
              fullWidth
              loading={
                isLoadingMyWorkspace || isFetchingStore || formik.isSubmitting
              }
              disabled={disabled || disabledError}
              onClick={onClickNext}
            >
              {t('button.next')}
            </Button>
          </Grid>
        </Grid>
      </ContainerBox>
      <ModalInfo
        open={showModal}
        onClose={() => {}}
        title={t('initial_setup.search_store.store_already_created_title')}
        description={t(
          'initial_setup.search_store.store_already_created_message'
        )}
        buttonActions={
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            width="100%"
            p="0 1.5rem"
          >
            <BackButton
              size="small"
              fullWidth={false}
              onClick={() => {
                setShowModal(false);
              }}
            />
            <Button
              variant="contained"
              color="primary"
              rounded
              onClick={() => {
                setShowModal(false);
                navigateToCheckList(true);
              }}
            >
              {t('initial_setup.search_store.update_store')}
            </Button>
          </Stack>
        }
      />
    </>
  );
}

export default SearchStoreSetUp;
